import {
  carryAfterSwitchReverted,
  carryAfterUndo,
  deliveredCarry,
} from "@cleo/shared/conversationCarryAfterUndo";
import { applyConversationHistoryEdit } from "@cleo/shared/conversationHistory";
import { retainMessagesAfterRevert } from "@cleo/shared/revertRetention";
import { pipe } from "effect/Function";
import * as Arr from "effect/Array";
import * as O from "effect/Order";
import * as Schema from "effect/Schema";
import {
  TURN_PROVENANCE_COMPLETED_ACTIVITY_KIND,
  TURN_PROVENANCE_STARTED_ACTIVITY_KIND,
  WorldTurnCharge,
} from "@cleo/contracts";
import type {
  MessageId,
  OrchestrationCheckpointSummary,
  OrchestrationEvent,
  OrchestrationLatestTurn,
  OrchestrationMessage,
  OrchestrationSession,
  OrchestrationThread,
  OrchestrationThreadActivity,
  TurnId,
} from "@cleo/contracts";

export type ThreadDetailReducerResult =
  | { readonly kind: "updated"; readonly thread: OrchestrationThread }
  | { readonly kind: "deleted" }
  | { readonly kind: "unchanged" };

const proposedPlanOrder = O.combine<OrchestrationThread["proposedPlans"][number]>(
  O.mapInput(O.String, (p) => p.createdAt),
  O.mapInput(O.String, (p) => p.id),
);

const checkpointOrder = O.mapInput(
  O.Number,
  (cp: OrchestrationThread["checkpoints"][number]) =>
    cp.checkpointTurnCount ?? Number.MAX_SAFE_INTEGER,
);

const activityOrder = O.combineAll<OrchestrationThreadActivity>([
  O.mapInput(O.Number, (a) => a.sequence ?? Number.MAX_SAFE_INTEGER),
  O.mapInput(O.String, (a) => a.createdAt),
  O.mapInput(O.String, (a) => a.id),
]);

// UP5 (after pingdotgg/t3code #9152, MIT): a per-array id index so the live
// append path can refuse a re-delivered id without rescanning the history.
// Only arrays this reducer produced are indexed; presence also proves the
// array is activityOrder-sorted, which snapshot-loaded arrays (DB order,
// null sequences first) are not, so those take the sorting path once.
const activityIdIndex = new WeakMap<
  ReadonlyArray<OrchestrationThreadActivity>,
  Set<OrchestrationThreadActivity["id"]>
>();

/**
 * Matches the validity rule in `deriveLatestContextWindowSnapshot` (and the
 * server's snapshot-side `dropStaleContextWindowActivities`): rows without a
 * finite, non-negative `usedTokens` are skipped during the consumer's backward
 * walk, so they must not replace an earlier resolvable row here.
 */
function isResolvableContextWindowActivity(activity: OrchestrationThreadActivity): boolean {
  if (activity.kind !== "context-window.updated") {
    return false;
  }
  const payload =
    activity.payload && typeof activity.payload === "object"
      ? (activity.payload as Record<string, unknown>)
      : null;
  const usedTokens = payload?.usedTokens;
  return typeof usedTokens === "number" && Number.isFinite(usedTokens) && usedTokens >= 0;
}

/**
 * Apply a single orchestration event to an `OrchestrationThread`, returning
 * the updated thread, a deletion signal, or an "unchanged" marker when the
 * event doesn't affect this thread.
 *
 * This is a pure reducer operating on contract types. UI-specific mapping
 * (e.g. resolving attachment preview URLs, normalising model slugs, adding
 * scoped fields like `environmentId`) is the caller's responsibility.
 */
export function applyThreadDetailEvent(
  thread: OrchestrationThread,
  event: OrchestrationEvent,
): ThreadDetailReducerResult {
  switch (event.type) {
    // ── Project events (irrelevant to thread detail) ────────────────
    case "project.created":
    case "project.meta-updated":
    case "project.deleted":
      return { kind: "unchanged" };

    // ── Thread lifecycle ────────────────────────────────────────────
    case "thread.created":
      return {
        kind: "updated",
        thread: {
          id: event.payload.threadId,
          projectId: event.payload.projectId,
          worldKey: event.payload.worldKey ?? null,
          sessionId: event.payload.sessionId ?? null,
          title: event.payload.title,
          parentThreadId: event.payload.parentThreadId ?? null,
          // Fork (Can 46): the inherited entries follow in
          // thread.history-inherited events.
          ...(event.payload.forkedFrom !== undefined
            ? {
                forkedFrom: {
                  threadId: event.payload.forkedFrom.threadId,
                  messageId: event.payload.forkedFrom.messageId,
                  sourceTitle: event.payload.forkedFrom.sourceTitle,
                  inheritedCount: event.payload.forkedFrom.inheritedCount,
                },
                conversationCarry: {
                  state: "pending" as const,
                  reason: "fork" as const,
                  handoffId: null,
                },
                inheritedMessages: [],
                inheritedOmitted: event.payload.forkedFrom.inheritedOmitted,
              }
            : {}),
          ...(event.payload.helper !== undefined ? { helper: event.payload.helper } : {}),
          workroomKey: event.payload.workroomKey ?? null,
          modelSelection: event.payload.modelSelection,
          localWorkPolicy: event.payload.localWorkPolicy ?? null,
          runtimeMode: event.payload.runtimeMode,
          interactionMode: event.payload.interactionMode,
          branch: event.payload.branch,
          worktreePath: event.payload.worktreePath,
          latestTurn: null,
          createdAt: event.payload.createdAt,
          updatedAt: event.payload.updatedAt,
          archivedAt: null,
          settledOverride: null,
          settledAt: null,
          snoozedUntil: null,
          snoozedAt: null,
          activeOrderKey: null,
          deletedAt: null,
          messages: [],
          proposedPlans: [],
          activities: [],
          checkpoints: [],
          session: null,
        },
      };

    case "thread.history-inherited": {
      // A replayed chunk replaces its own entries; order is the ordinal.
      const incoming = new Set(event.payload.entries.map((entry) => entry.ordinal));
      const inheritedMessages = [
        ...(thread.inheritedMessages ?? []).filter((entry) => !incoming.has(entry.ordinal)),
        ...event.payload.entries,
      ].toSorted((left, right) => left.ordinal - right.ordinal);
      return { kind: "updated", thread: { ...thread, inheritedMessages } };
    }

    case "thread.conversation-carry-delivered":
      return thread.conversationCarry
        ? {
            kind: "updated",
            thread: {
              ...thread,
              conversationCarry: deliveredCarry(thread.conversationCarry, event.payload.handoffId),
            },
          }
        : { kind: "unchanged" };

    // Can 21: mirrors the server projector. The selection follows the switch;
    // the conversation so far is owed to it unless a carry is still pending
    // (a fork not yet continued keeps its own); a revert drops it, unless an
    // undo still owes the bound engine a fresh start (W-79778d2b).
    case "thread.engine-switched":
      return {
        kind: "updated",
        thread: {
          ...thread,
          modelSelection: event.payload.to,
          conversationCarry:
            event.payload.reverted === true
              ? carryAfterSwitchReverted(thread.conversationCarry ?? null)
              : thread.conversationCarry?.state === "pending"
                ? thread.conversationCarry
                : { state: "pending", reason: "engine-switch", handoffId: null },
          updatedAt: event.payload.switchedAt,
        },
      };

    case "thread.deleted":
      return { kind: "deleted" };

    case "thread.archived":
      return {
        kind: "updated",
        thread: {
          ...thread,
          archivedAt: event.payload.archivedAt,
          titleRegeneration: null,
          updatedAt: event.payload.updatedAt,
        },
      };

    case "thread.unarchived":
      return {
        kind: "updated",
        thread: { ...thread, archivedAt: null, updatedAt: event.payload.updatedAt },
      };

    case "thread.settled":
      return {
        kind: "updated",
        thread: {
          ...thread,
          settledOverride: "settled",
          settledAt: event.payload.settledAt,
          activeOrderKey: null,
          updatedAt: event.payload.updatedAt,
        },
      };

    case "thread.unsettled":
      return {
        kind: "updated",
        thread: {
          ...thread,
          settledOverride: event.payload.reason === "user" ? "active" : null,
          settledAt: null,
          updatedAt: event.payload.updatedAt,
        },
      };

    case "thread.snoozed":
      return {
        kind: "updated",
        thread: {
          ...thread,
          snoozedUntil: event.payload.snoozedUntil,
          snoozedAt: event.payload.snoozedAt,
          updatedAt: event.payload.updatedAt,
        },
      };

    case "thread.unsnoozed":
      return {
        kind: "updated",
        thread: {
          ...thread,
          snoozedUntil: null,
          snoozedAt: null,
          updatedAt: event.payload.updatedAt,
        },
      };

    case "thread.pinned":
      return {
        kind: "updated",
        thread: {
          ...thread,
          pinnedAt: event.payload.pinnedAt,
          ...(event.payload.pinOrderKey !== undefined
            ? { pinOrderKey: event.payload.pinOrderKey }
            : {}),
          updatedAt: event.payload.updatedAt,
        },
      };

    case "thread.unpinned":
      return {
        kind: "updated",
        thread: {
          ...thread,
          pinnedAt: null,
          pinOrderKey: null,
          updatedAt: event.payload.updatedAt,
        },
      };

    case "thread.pin-reordered":
      return {
        kind: "updated",
        thread: {
          ...thread,
          pinOrderKey: event.payload.orderKey,
          updatedAt: event.payload.updatedAt,
        },
      };

    // ── Thread metadata ─────────────────────────────────────────────
    case "thread.meta-updated":
      return {
        kind: "updated",
        thread: {
          ...thread,
          ...(event.payload.title !== undefined ? { title: event.payload.title } : {}),
          ...(event.payload.titleRegeneration !== undefined
            ? { titleRegeneration: event.payload.titleRegeneration }
            : {}),
          ...(event.payload.modelSelection !== undefined
            ? { modelSelection: event.payload.modelSelection }
            : {}),
          ...(event.payload.branch !== undefined ? { branch: event.payload.branch } : {}),
          ...(event.payload.worktreePath !== undefined
            ? { worktreePath: event.payload.worktreePath }
            : {}),
          ...(event.payload.activeOrderKey !== undefined
            ? { activeOrderKey: event.payload.activeOrderKey }
            : {}),
          updatedAt: event.payload.updatedAt,
        },
      };

    case "thread.runtime-mode-set":
      return {
        kind: "updated",
        thread: {
          ...thread,
          runtimeMode: event.payload.runtimeMode,
          updatedAt: event.payload.updatedAt,
        },
      };

    case "thread.interaction-mode-set":
      return {
        kind: "updated",
        thread: {
          ...thread,
          interactionMode: event.payload.interactionMode,
          updatedAt: event.payload.updatedAt,
        },
      };

    // ── Turn lifecycle ──────────────────────────────────────────────
    case "thread.turn-start-requested":
      return {
        kind: "updated",
        thread: {
          ...thread,
          ...(event.payload.modelSelection !== undefined
            ? { modelSelection: event.payload.modelSelection }
            : {}),
          runtimeMode: event.payload.runtimeMode,
          interactionMode: event.payload.interactionMode,
          updatedAt: event.occurredAt,
        },
      };

    case "thread.turn-interrupt-requested": {
      if (event.payload.turnId === undefined) {
        return { kind: "unchanged" };
      }
      const latestTurn = thread.latestTurn;
      if (latestTurn === null || latestTurn.turnId !== event.payload.turnId) {
        return { kind: "unchanged" };
      }
      return {
        kind: "updated",
        thread: {
          ...thread,
          latestTurn: {
            ...latestTurn,
            state: "interrupted",
            startedAt: latestTurn.startedAt ?? event.payload.createdAt,
            completedAt: latestTurn.completedAt ?? event.payload.createdAt,
          },
          updatedAt: event.occurredAt,
        },
      };
    }

    // ── Messages ────────────────────────────────────────────────────
    case "thread.message-reaction-set":
      return {
        kind: "updated",
        thread: {
          ...thread,
          messages: thread.messages.map((message) =>
            message.id === event.payload.messageId
              ? { ...message, reactions: event.payload.reactions }
              : message,
          ),
        },
      };

    case "thread.message-sent": {
      const message: OrchestrationMessage = {
        id: event.payload.messageId,
        role: event.payload.role,
        text: event.payload.text,
        ...(event.payload.reactionContext !== undefined
          ? { reactionContext: event.payload.reactionContext }
          : {}),
        ...(event.payload.attachments !== undefined
          ? { attachments: event.payload.attachments }
          : {}),
        ...(event.payload.contextResolution !== undefined
          ? { contextResolution: event.payload.contextResolution }
          : {}),
        turnId: event.payload.turnId,
        streaming: event.payload.streaming,
        createdAt: event.payload.createdAt,
        updatedAt: event.payload.updatedAt,
      };

      // One pass over the list (UP5, after #11029): a streamed delta no
      // longer scans for the message and then maps every entry a second time.
      // Untouched entries keep their references; a new message appends once.
      let found = false;
      let messages: OrchestrationThread["messages"] = thread.messages.map((entry) => {
        if (entry.id !== message.id) return entry;
        found = true;
        return {
          ...entry,
          text: message.streaming
            ? `${entry.text}${message.text}`
            : message.text.length > 0
              ? message.text
              : entry.text,
          streaming: message.streaming,
          ...(message.turnId !== undefined ? { turnId: message.turnId } : {}),
          ...(message.streaming ? {} : { updatedAt: message.updatedAt }),
          ...(message.attachments !== undefined ? { attachments: message.attachments } : {}),
          ...(message.contextResolution !== undefined
            ? { contextResolution: message.contextResolution }
            : {}),
        };
      });
      if (!found) messages = Arr.append(messages, message);
      // Fast providers may complete before buffered assistant text is emitted.
      // Reuse the retained completion for this exact turn when its message
      // arrives; the activity handler covers the opposite delivery order.
      if (message.role === "assistant" && message.turnId !== null) {
        const completed = thread.activities.findLast(
          (activity) =>
            activity.kind === TURN_PROVENANCE_COMPLETED_ACTIVITY_KIND &&
            activity.turnId === message.turnId,
        );
        if (completed)
          messages = foldTurnProvenanceIntoMessages(messages, thread.activities, completed);
      }
      // Update latestTurn for assistant messages bound to a turn. A completed
      // assistant message only settles the turn once the session is no longer
      // running it — providers may emit several assistant messages per turn
      // (commentary between tool calls), and the turn must stay unsettled
      // until the provider reports turn end.
      const turnStillRunning =
        event.payload.turnId !== null &&
        thread.session?.status === "running" &&
        thread.session.activeTurnId === event.payload.turnId;
      const settlesTurn = !event.payload.streaming && !turnStillRunning;
      const latestTurn: OrchestrationThread["latestTurn"] =
        event.payload.role === "assistant" &&
        event.payload.turnId !== null &&
        (thread.latestTurn === null || thread.latestTurn.turnId === event.payload.turnId)
          ? {
              turnId: event.payload.turnId,
              ...(thread.latestTurn?.turnId === event.payload.turnId &&
              thread.latestTurn.userMessageId
                ? { userMessageId: thread.latestTurn.userMessageId }
                : {}),
              state: settlesTurn
                ? thread.latestTurn?.state === "interrupted"
                  ? "interrupted"
                  : thread.latestTurn?.state === "error"
                    ? "error"
                    : "completed"
                : "running",
              requestedAt:
                thread.latestTurn?.turnId === event.payload.turnId
                  ? thread.latestTurn.requestedAt
                  : event.payload.createdAt,
              startedAt:
                thread.latestTurn?.turnId === event.payload.turnId
                  ? (thread.latestTurn.startedAt ?? event.payload.createdAt)
                  : event.payload.createdAt,
              completedAt: settlesTurn
                ? event.payload.updatedAt
                : thread.latestTurn?.turnId === event.payload.turnId
                  ? (thread.latestTurn.completedAt ?? null)
                  : null,
              assistantMessageId: event.payload.messageId,
            }
          : thread.latestTurn;

      // Rebind checkpoint assistant message IDs for assistant messages.
      const checkpoints =
        event.payload.role === "assistant" && event.payload.turnId !== null
          ? rebindCheckpointAssistantMessage(
              thread.checkpoints,
              event.payload.turnId,
              event.payload.messageId,
            )
          : thread.checkpoints;

      return {
        kind: "updated",
        thread: {
          ...thread,
          messages,
          checkpoints,
          latestTurn,
          updatedAt: event.occurredAt,
        },
      };
    }

    // ── Session ─────────────────────────────────────────────────────
    case "thread.session-set": {
      // Leaving the "running" session status is the turn-end signal: settle a
      // still-running latest turn so its duration reflects the whole turn.
      const settledTurnState = settledTurnStateForSessionStatus(event.payload.session.status);
      const latestTurn: OrchestrationLatestTurn | null =
        event.payload.session.status === "running" && event.payload.session.activeTurnId !== null
          ? {
              turnId: event.payload.session.activeTurnId,
              ...(thread.latestTurn?.turnId === event.payload.session.activeTurnId &&
              thread.latestTurn.userMessageId
                ? { userMessageId: thread.latestTurn.userMessageId }
                : {}),
              state: "running",
              requestedAt:
                thread.latestTurn?.turnId === event.payload.session.activeTurnId
                  ? thread.latestTurn.requestedAt
                  : event.payload.session.updatedAt,
              startedAt:
                thread.latestTurn?.turnId === event.payload.session.activeTurnId
                  ? (thread.latestTurn.startedAt ?? event.payload.session.updatedAt)
                  : event.payload.session.updatedAt,
              completedAt: null,
              assistantMessageId:
                thread.latestTurn?.turnId === event.payload.session.activeTurnId
                  ? thread.latestTurn.assistantMessageId
                  : null,
            }
          : thread.latestTurn !== null &&
              thread.latestTurn.state === "running" &&
              settledTurnState !== null
            ? {
                ...thread.latestTurn,
                state: settledTurnState,
                // A running turn's completedAt can only hold a mid-turn
                // placeholder checkpoint timestamp — the session leaving
                // "running" is the authoritative turn end.
                completedAt: event.payload.session.updatedAt,
              }
            : thread.latestTurn;

      return {
        kind: "updated",
        thread: {
          ...thread,
          session: event.payload.session,
          latestTurn,
          updatedAt: event.occurredAt,
        },
      };
    }

    case "thread.session-stop-requested":
      return thread.session === null
        ? { kind: "unchanged" }
        : {
            kind: "updated",
            thread: {
              ...thread,
              session: {
                ...thread.session,
                status: "stopped",
                activeTurnId: null,
                updatedAt: event.payload.createdAt,
              },
              updatedAt: event.occurredAt,
            },
          };

    // ── Proposed plans ──────────────────────────────────────────────
    case "thread.proposed-plan-upserted": {
      const proposedPlan = event.payload.proposedPlan;

      const proposedPlans = pipe(
        thread.proposedPlans,
        Arr.filter((entry) => entry.id !== proposedPlan.id),
        Arr.append(proposedPlan),
        Arr.sort(proposedPlanOrder),
      );

      return {
        kind: "updated",
        thread: { ...thread, proposedPlans, updatedAt: event.occurredAt },
      };
    }

    // ── Checkpoints / turn diffs ────────────────────────────────────
    case "thread.turn-diff-completed": {
      const checkpoint: OrchestrationCheckpointSummary = {
        turnId: event.payload.turnId,
        checkpointTurnCount: event.payload.checkpointTurnCount,
        checkpointRef: event.payload.checkpointRef,
        status: event.payload.status,
        files: event.payload.files,
        assistantMessageId: event.payload.assistantMessageId,
        completedAt: event.payload.completedAt,
      };

      const existing = thread.checkpoints.find((entry) => entry.turnId === checkpoint.turnId);
      // Don't overwrite a non-missing checkpoint with a missing one.
      if (existing && existing.status !== "missing" && checkpoint.status === "missing") {
        return { kind: "unchanged" };
      }

      const checkpoints = pipe(
        thread.checkpoints,
        Arr.filter((entry) => entry.turnId !== checkpoint.turnId),
        Arr.append(checkpoint),
        Arr.sort(checkpointOrder),
      );

      // Mid-turn diff updates produce placeholder checkpoints; record the
      // checkpoint, but don't settle a turn its session is still running.
      const diffTurnStillRunning =
        thread.session?.status === "running" &&
        thread.session.activeTurnId === event.payload.turnId;
      const latestTurn =
        !diffTurnStillRunning &&
        (thread.latestTurn === null || thread.latestTurn.turnId === event.payload.turnId)
          ? {
              turnId: event.payload.turnId,
              ...(thread.latestTurn?.turnId === event.payload.turnId &&
              thread.latestTurn.userMessageId
                ? { userMessageId: thread.latestTurn.userMessageId }
                : {}),
              state: checkpointStatusToTurnState(event.payload.status),
              requestedAt: thread.latestTurn?.requestedAt ?? event.payload.completedAt,
              startedAt: thread.latestTurn?.startedAt ?? event.payload.completedAt,
              completedAt: event.payload.completedAt,
              assistantMessageId: event.payload.assistantMessageId,
            }
          : thread.latestTurn;

      return {
        kind: "updated",
        thread: { ...thread, checkpoints, latestTurn, updatedAt: event.occurredAt },
      };
    }

    // ── Revert ──────────────────────────────────────────────────────
    case "thread.history-edited":
      return {
        kind: "updated",
        thread: {
          ...applyConversationHistoryEdit(thread, event.payload),
          updatedAt: event.occurredAt,
        },
      };

    case "thread.reverted": {
      const checkpoints = pipe(
        thread.checkpoints,
        Arr.filter(
          (entry) =>
            entry.checkpointTurnCount !== undefined &&
            entry.checkpointTurnCount <= event.payload.turnCount,
        ),
        Arr.sort(checkpointOrder),
      );

      const retainedTurnIds = new Set(Arr.map(checkpoints, (entry) => entry.turnId));
      // W-2af5e7f1: exactly the messages the server's revert removed, whatever
      // part of the conversation is loaded; an older event names none.
      const removed =
        event.payload.removedMessageIds !== undefined
          ? new Set<string>(event.payload.removedMessageIds)
          : undefined;
      const messages =
        removed !== undefined
          ? Arr.filter(thread.messages, (message) => !removed.has(message.id))
          : retainMessagesAfterRevert(thread.messages, retainedTurnIds, event.payload.turnCount);
      const proposedPlans = pipe(
        thread.proposedPlans,
        Arr.filter((plan) => plan.turnId === null || retainedTurnIds.has(plan.turnId)),
      );
      const activities = pipe(
        thread.activities,
        Arr.filter((activity) => activity.turnId === null || retainedTurnIds.has(activity.turnId)),
      );
      const latestCheckpoint = checkpoints.at(-1) ?? null;

      return {
        kind: "updated",
        thread: {
          ...thread,
          checkpoints,
          messages,
          proposedPlans,
          activities,
          latestTurn:
            latestCheckpoint === null
              ? null
              : {
                  turnId: latestCheckpoint.turnId,
                  state: checkpointStatusToTurnState(
                    latestCheckpoint.status as "ready" | "missing" | "error",
                  ),
                  requestedAt: latestCheckpoint.completedAt,
                  startedAt: latestCheckpoint.completedAt,
                  completedAt: latestCheckpoint.completedAt,
                  assistantMessageId: latestCheckpoint.assistantMessageId ?? null,
                },
          // W-79778d2b: the server's projection owes the engine a fresh start; so does this copy.
          ...(event.payload.conversationCarry === true
            ? { conversationCarry: carryAfterUndo(thread.conversationCarry ?? null) }
            : {}),
          updatedAt: event.occurredAt,
        },
      };
    }

    // ── Activities ──────────────────────────────────────────────────
    case "thread.activity-appended": {
      const activity = event.payload.activity;
      // A resolvable context-window update supersedes earlier resolvable ones
      // for the same turn: consumers only read the latest value (walking the
      // array backwards), and providers stream these updates continuously, so
      // retaining the history grows the thread by thousands of rows over a
      // long session. Mirrors the server-side snapshot rule in
      // dropStaleContextWindowActivities; retention stays per turn so a
      // thread.reverted that discards turns can still resolve a value from
      // the turns that survive.
      const supersedesContextWindow = isResolvableContextWindowActivity(activity);
      // Live streams append in order: an unseen id sorting at or after the
      // tail of a known-sorted array appends without re-filtering and
      // re-sorting the whole history on every event (#9152). The id set moves
      // forward to the new array; a superseded array falls back to sorting.
      const knownIds = activityIdIndex.get(thread.activities);
      const lastActivity = thread.activities.at(-1);
      if (
        !supersedesContextWindow &&
        knownIds !== undefined &&
        (lastActivity === undefined || activityOrder(lastActivity, activity) <= 0) &&
        !knownIds.has(activity.id)
      ) {
        const appended = Arr.append(thread.activities, activity);
        activityIdIndex.delete(thread.activities);
        knownIds.add(activity.id);
        activityIdIndex.set(appended, knownIds);
        const appendedMessages =
          activity.kind === TURN_PROVENANCE_COMPLETED_ACTIVITY_KIND && activity.turnId !== null
            ? foldTurnProvenanceIntoMessages(thread.messages, appended, activity)
            : thread.messages;
        return {
          kind: "updated",
          thread: {
            ...thread,
            activities: appended,
            messages: appendedMessages,
            updatedAt: event.occurredAt,
          },
        };
      }
      const activities = pipe(
        thread.activities,
        Arr.filter(
          (entry) =>
            entry.id !== activity.id &&
            !(
              supersedesContextWindow &&
              entry.turnId === activity.turnId &&
              isResolvableContextWindowActivity(entry)
            ),
        ),
        Arr.append(activity),
        Arr.sort(activityOrder),
      );
      activityIdIndex.set(activities, new Set(activities.map((entry) => entry.id)));

      // Arc M1 live fold: the snapshot query joins projection_turns provenance
      // into message.turnProvenance, but a live session only ever sees the
      // turn.provenance.* activity events (excluded from every projection
      // except projection_turns, and hidden from timelines by timelineBypass).
      // Fold the completed record — plus the latest started record for the
      // same turn, which carries the applied model and effort — into the
      // turn's assistant messages so the provenance chip resolves without a
      // thread reload.
      const messages =
        activity.kind === TURN_PROVENANCE_COMPLETED_ACTIVITY_KIND && activity.turnId !== null
          ? foldTurnProvenanceIntoMessages(thread.messages, activities, activity)
          : thread.messages;

      return {
        kind: "updated",
        thread: { ...thread, activities, messages, updatedAt: event.occurredAt },
      };
    }

    // ── Events that don't mutate thread state directly ──────────────
    case "thread.approval-response-requested":
    case "thread.user-input-response-requested":
    case "thread.checkpoint-revert-requested":
      return { kind: "unchanged" };
  }

  // Forward-compatible: ignore unrecognized event types.
  return { kind: "unchanged" };
}

// ── Helpers ──────────────────────────────────────────────────────────

function provenanceString(value: unknown): string | null {
  if (typeof value !== "string") {
    return null;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

const isWorldTurnCharge = Schema.is(WorldTurnCharge);

function provenancePayloadRecord(activity: OrchestrationThreadActivity): Record<string, unknown> {
  return typeof activity.payload === "object" && activity.payload !== null
    ? (activity.payload as Record<string, unknown>)
    : {};
}

function foldTurnProvenanceIntoMessages(
  messages: OrchestrationThread["messages"],
  activities: ReadonlyArray<OrchestrationThreadActivity>,
  completed: OrchestrationThreadActivity,
): OrchestrationThread["messages"] {
  const turnId = completed.turnId;
  const completedPayload = provenancePayloadRecord(completed);
  // Latest started record wins: an explicit model.rerouted correction is
  // logged as a later started-kind activity for the same turn.
  let startedPayload: Record<string, unknown> = {};
  for (let index = activities.length - 1; index >= 0; index -= 1) {
    const entry = activities[index];
    if (
      entry !== undefined &&
      entry.kind === TURN_PROVENANCE_STARTED_ACTIVITY_KIND &&
      entry.turnId === turnId
    ) {
      startedPayload = provenancePayloadRecord(entry);
      break;
    }
  }

  // Field-name casts mirror turnProvenanceFromPersisted: raw activity payload
  // values verbatim, requested* left null for the snapshot to fill on reload.
  const folded = {
    kind: "known" as const,
    driverKind: provenanceString(completedPayload["provider"] ?? startedPayload["provider"]),
    providerInstanceId: provenanceString(
      completedPayload["providerInstanceId"] ?? startedPayload["providerInstanceId"],
    ),
    requestedInstanceId: null,
    requestedModel: null,
    requestedOptions: null,
    model: provenanceString(startedPayload["model"]),
    effort: provenanceString(startedPayload["effort"]),
    usage: completedPayload["usage"] ?? null,
    modelUsage: completedPayload["modelUsage"] ?? null,
    totalCostUsd:
      typeof completedPayload["totalCostUsd"] === "number" &&
      Number.isFinite(completedPayload["totalCostUsd"])
        ? completedPayload["totalCostUsd"]
        : null,
    // W-4be62b27: what the World charged for a managed turn, verbatim.
    worldCharge: isWorldTurnCharge(completedPayload["worldCharge"])
      ? completedPayload["worldCharge"]
      : null,
  } as unknown as NonNullable<OrchestrationMessage["turnProvenance"]>;

  return Arr.map(messages, (message) => {
    if (message.turnId !== turnId || message.role !== "assistant") {
      return message;
    }
    const existing = message.turnProvenance;
    if (existing === undefined || existing.kind === "unknown") {
      return { ...message, turnProvenance: folded };
    }
    // Snapshot-provided non-null fields win; the fold fills the rest.
    const merged = {
      ...folded,
      ...Object.fromEntries(Object.entries(existing).filter(([, value]) => value !== null)),
    } as typeof existing;
    return { ...message, turnProvenance: merged };
  });
}

/**
 * Turn state to settle a still-running latest turn with when its session
 * leaves the "running" status, or null while the session is (re)starting or
 * running and the turn must stay unsettled.
 */
function settledTurnStateForSessionStatus(
  status: OrchestrationSession["status"],
): "completed" | "interrupted" | "error" | null {
  switch (status) {
    case "idle":
    case "ready":
      return "completed";
    case "error":
      return "error";
    case "interrupted":
    case "stopped":
      return "interrupted";
    case "starting":
    case "running":
      return null;
  }
}

function checkpointStatusToTurnState(
  status: "ready" | "missing" | "error",
): OrchestrationLatestTurn["state"] {
  switch (status) {
    case "ready":
      return "completed";
    case "error":
      return "error";
    case "missing":
      return "completed";
  }
}

function rebindCheckpointAssistantMessage(
  checkpoints: ReadonlyArray<OrchestrationCheckpointSummary>,
  turnId: TurnId,
  messageId: MessageId,
): OrchestrationCheckpointSummary[] {
  return Arr.map(checkpoints, (entry) =>
    entry.turnId === turnId ? { ...entry, assistantMessageId: messageId } : entry,
  );
}
